import React, { useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useTenantTranslation } from "hooks/formatters";
import { HeatMix, MaterialRead } from "src/store/api/generatedApi";
import { MixDisplayMode, mixViewFor } from "../types";
import { processBasketComparison } from "./basketComparisonUtils";
import { HeatBasketRow } from "./HeatBasketRow";
import { HeatNonBasketMaterialsRow } from "./HeatNonBasketMaterialsRow";

interface HeatBasketComparisonViewProps {
  mixes: HeatMix[];
  availableMixSources: string[];
  ignoredMaterials: Set<string>;
  materials: MaterialRead[];
  displayMode: MixDisplayMode;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { ease: "easeOut" as const },
  },
};

export const HeatBasketComparisonView: React.FC<
  HeatBasketComparisonViewProps
> = ({
  mixes,
  availableMixSources,
  ignoredMaterials,
  materials,
  displayMode,
}) => {
  const { t } = useTenantTranslation();

  const mixView = mixViewFor(displayMode);

  const filteredMixes = useMemo(
    () => mixes.filter((mix) => availableMixSources.includes(mix.source)),
    [mixes, availableMixSources]
  );

  const { baskets, nonBasketMaterials, hasNonBasketMaterials } = useMemo(
    () =>
      processBasketComparison(
        filteredMixes,
        ignoredMaterials,
        materials,
        mixView
      ),
    [filteredMixes, ignoredMaterials, materials, mixView]
  );

  if (baskets.length === 0 && !hasNonBasketMaterials) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ p: 2 }}>
        {t("noBasketData")}
      </Typography>
    );
  }

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Stack spacing={2} sx={{ px: 1, mt: 1 }}>
        {baskets.map((basket) => (
          <motion.div key={basket.basketNumber} variants={rowVariants}>
            <HeatBasketRow
              basket={basket}
              availableMixSources={availableMixSources}
              displayMode={displayMode}
            />
          </motion.div>
        ))}
        {hasNonBasketMaterials ? (
          <motion.div variants={rowVariants}>
            <HeatNonBasketMaterialsRow
              sources={nonBasketMaterials}
              availableMixSources={availableMixSources}
              displayMode={displayMode}
            />
          </motion.div>
        ) : null}
      </Stack>
    </motion.div>
  );
};
